/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Message } from 'primereact/message';
import { Dialog } from 'primereact/dialog';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { evalSuitesService } from '../../services/eval-suites.service';
import { evalCasesService } from '../../services/eval-cases.service';
import { promptsService } from '../../services/prompts.service';
import { projectsService } from '../../services/projects.service';
import { aiModelsService } from '../../services/ai-models.service';
import { ragsService } from '../../services/rags.service';
import type { EvalSuite, EvalCase } from '../../types';
import { extractErrorMessage } from '../../utils/error-handler';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import { AuditInfoCard } from '../../components/common/audit-info-card';
import { EmptyState, Metric, PageSkeleton, Rise, SectionLabel, StatusChip } from '../../components/common/panel';
import ScreenHeader from '../../components/common/screen-header';
import { PANEL, PRETTY, riseAt } from '../../components/common/ui-kit';
import { useCanUpdate, useCanDelete, useHasPermission } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { PERMISSIONS } from '../../constants/permissions';
import EvalRunTriggerPanel from '../../components/ai-runs/eval-run-trigger-panel';
import DatasetImportDialog from '../../components/ai-runs/dataset-import-dialog';

const EvalView = () => {
  const { id, isReady, navigateToEdit, navigateToList, goBack } = useSecureNavigation({
    entity: 'eval',
    basePath: '/evals',
  });
  // Back returns to where you came from (e.g. a RAG that links here), falling back
  // to the list only on a cold load / deep-link with no in-app history.
  const smartBack = () => (((window.history.state as any)?.idx ?? 0) > 0 ? goBack() : navigateToList());

  const canUpdate = useCanUpdate(JOB_NAMES.EVALS);
  const canDelete = useCanDelete(JOB_NAMES.EVALS);
  const canRunEval = useHasPermission(PERMISSIONS.EVAL_RUN_EXECUTE);

  const [suiteData, setSuiteData] = useState<EvalSuite | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [caseFilter, setCaseFilter] = useState('');
  const [showAllCases, setShowAllCases] = useState(false);
  const queryClient = useQueryClient();

  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });
  const { data: aiModelsResp } = useQuery({
    queryKey: ['ai-models', 'names'],
    queryFn: () => aiModelsService.list({ page: 1, page_size: 100 }),
    staleTime: 5 * 60 * 1000,
  });
  // Resolve THIS suite's cases by id. It used to page the whole library 500 at a
  // time (up to 40 serial calls) looking for them; the list now takes `ids`.
  const suiteCaseIds = useMemo(
    () => (suiteData?.cases || []).map((c) => String(c.caseRef)), [suiteData]);
  const { data: caseById = {} } = useQuery<Record<string, EvalCase>>({
    queryKey: ['eval-cases', 'for-suite', id, suiteCaseIds.join(',')],
    enabled: suiteCaseIds.length > 0,
    staleTime: 60 * 1000,
    queryFn: async () => {
      // 100 ids per request keeps the URL well under server limits; batches run together.
      const batches: string[][] = [];
      for (let i = 0; i < suiteCaseIds.length; i += 100) batches.push(suiteCaseIds.slice(i, i + 100));
      const pages = await Promise.all(batches.map((ids) =>
        evalCasesService.list({ ids: ids.join(','), page: 1, page_size: ids.length })));
      const map: Record<string, EvalCase> = {};
      for (const res of pages) for (const c of res?.data || []) map[c._id] = c;
      return map;
    },
  });
  // Resolve the suite's target prompt name for display.
  const { data: promptsResp } = useQuery({
    queryKey: ['prompts', 'names'],
    queryFn: () => promptsService.list({ page: 1, page_size: 100 }),
    staleTime: 5 * 60 * 1000,
  });
  const promptName = (id?: string | null) => {
    if (!id) return null;
    const p = (promptsResp?.data || []).find((x) => x._id === id);
    return p ? p.name : id;
  };

  // A rag target used to print its raw ObjectId. Resolve it to the RAG's code,
  // matching what the run result screen shows for the same target.
  const targetRagId = suiteData?.targetRef?.type === 'rag' ? suiteData?.targetRef?.id : null;
  const { data: targetRag, isLoading: ragLoading } = useQuery({
    queryKey: ['rag', targetRagId],
    queryFn: () => ragsService.getById(String(targetRagId)),
    enabled: !!targetRagId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const projectLabel = (appId?: string | null) => {
    if (!appId) return 'Global / Shared';
    const p = accessibleProjects.find((x) => x._id === appId);
    return p ? (p.appCode ? `${p.name} (${p.appCode})` : p.name) : appId;
  };
  const modelLabel = (mid?: string | null) => {
    if (!mid) return '-';
    const m = (aiModelsResp?.data || []).find((x) => x._id === mid);
    return m ? m.displayName : mid;
  };

  useBreadcrumbTitle(suiteData?.name);

  useEffect(() => {
    if (!isReady || !id) return;
    const fetchSuite = async () => {
      try {
        setLoading(true);
        const data = await evalSuitesService.getById(id);
        setSuiteData(data);
      } catch (err: any) {
        setError(extractErrorMessage(err, 'Failed to fetch eval suite details'));
      } finally {
        setLoading(false);
      }
    };
    fetchSuite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, id]);

  const reloadSuite = async () => {
    if (!id) return;
    setSuiteData(await evalSuitesService.getById(id));
    queryClient.invalidateQueries({ queryKey: ['eval-cases'] });
  };

  const handleEdit = () => id && navigateToEdit(id);
  const handleDeleteClick = () => setDeleteDialogOpen(true);

  const handleDeleteConfirm = async () => {
    if (!id) return;
    try {
      await evalSuitesService.softDelete(id);
      setDeleteDialogOpen(false);
      navigateToList();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete eval suite'));
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => setDeleteDialogOpen(false);

  if (loading) return <PageSkeleton panels={2} />;

  if (error || !suiteData) {
    return (
      <div className="p-4">
        <Message severity="error" text={error || 'Eval suite not found'} className="mb-4 w-full" />
        <Button
          label="Back to Suites"
          icon="pi pi-arrow-left"
          severity="secondary"
          outlined
          onClick={() => navigateToList()}
        />
      </div>
    );
  }

  const g = suiteData.grader as any;
  const metrics: any[] = (suiteData as any).metrics || [];
  // The single `grader` block describes a suite written before named metrics.
  // When `metrics` exists the engine ignores it entirely (eval_run_service
  // _metrics_for), so printing it here claimed a judge that never runs.
  const hasGrader = !!g?.type && metrics.length === 0;
  const overallPass = (suiteData as any).passThreshold ?? g?.passThreshold ?? null;
  const cases = [...(suiteData.cases || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <div className="p-4" data-testid="SCR-EvalSuite-View">
      <ScreenHeader
        icon="pi pi-check-circle"
        onBack={smartBack}
        title={suiteData.name}
        right={
          <>
            {suiteData.suiteCode && <span className="font-mono text-xs text-ink-muted">{suiteData.suiteCode}</span>}
            <StatusChip tone="info" dot={false}>{suiteData.type}</StatusChip>
            {suiteData.version != null && (
              <StatusChip tone="neutral" dot={false} className="tabular-nums">v{suiteData.version}</StatusChip>
            )}
            {!(suiteData as any).isDelete && (
              <>
                {canRunEval && (
                  <span className="ui-press inline-flex">
                    <Button label="Run" icon="pi pi-play" severity="success" onClick={() => setRunDialogOpen(true)} size="small" data-testid="EvalView.Button.Run" />
                  </span>
                )}
                {canUpdate && (
                  <span className="ui-press inline-flex">
                    <Button label="Import dataset" icon="pi pi-file-import" severity="secondary" outlined
                            onClick={() => setImportOpen(true)} size="small" data-testid="EvalView.Button.Import" />
                  </span>
                )}
                {canUpdate && <span className="ui-press inline-flex"><Button label="Edit" icon="pi pi-pencil" onClick={handleEdit} size="small" /></span>}
                {canDelete && <span className="ui-press inline-flex"><Button label="Delete" icon="pi pi-trash" severity="danger" onClick={handleDeleteClick} size="small" /></span>}
              </>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 gap-3">
        <Rise i={1} className={`${PANEL} p-4`}>
          <SectionLabel icon="pi pi-info-circle">Suite</SectionLabel>
          <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-4 lg:grid-cols-4">
            <Metric label="Suite code" value={suiteData.suiteCode || '—'} mono hint={suiteData.suiteCode} />
            <Metric label="Type" value={suiteData.type || '—'} />
            <Metric label="Version" value={suiteData.version != null ? `v${suiteData.version}` : '—'} />
            <Metric label="Project" value={projectLabel((suiteData as any).appId)} />
          </div>

          <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            {hasGrader && (
              <div className="min-w-0">
                <SectionLabel icon="pi pi-verified">Grader</SectionLabel>
                <p className="mt-1 text-sm text-ink">
                  {g.type}
                  {g.passThreshold != null ? <span className="tabular-nums">{` · pass ≥ ${g.passThreshold}`}</span> : ''}
                  {g.type === 'llm_judge' && g.model ? ` · judge: ${modelLabel(g.model)}` : ''}
                  {(g.type === 'exact' || g.type === 'contains') && g.caseSensitive ? ' · case-sensitive' : ''}
                </p>
                {g.type === 'llm_judge' && g.instructions && (
                  <p className="mt-1 max-w-prose whitespace-pre-wrap text-xs leading-relaxed text-ink-muted" style={PRETTY}>{g.instructions}</p>
                )}
              </div>
            )}
            <div className="min-w-0">
              <SectionLabel icon="pi pi-crosshairs">Runs against</SectionLabel>
              <p className="mt-1 text-sm text-ink">
                {(() => {
                  const ref = suiteData.targetRef as any;
                  if (!ref?.type || !ref?.id) return "Case input sent to the run's own model";
                  const version = ref.version != null ? ` · v${ref.version}` : '';
                  if (ref.type === 'prompt') return <>Prompt: <span className="font-medium">{promptName(ref.id)}</span></>;
                  if (ref.type === 'model') return <>Model: <span className="font-medium">{modelLabel(ref.id)}</span></>;
                  if (ref.type === 'rag') {
                    return (
                      <>
                        {/* Not the raw ObjectId while the name loads — it flashed on every visit. */}
                        RAG: <span className="font-medium">{ragLoading ? '…' : (targetRag as any)?.ragCode || (targetRag as any)?.name || ref.id}</span>
                        <span className="tabular-nums text-ink-muted">{version}</span>
                      </>
                    );
                  }
                  return <>{ref.type}: <span className="font-mono text-[13px] font-medium">{ref.id}</span></>;
                })()}
              </p>
              {/* The call the model is tested under, so a reviewer comparing two
                  model suites can see they asked the same thing the same way. */}
              {suiteData.targetRef?.type === 'model' && (suiteData.targetRef.system || suiteData.targetRef.params) && (
                <div className="mt-1.5 flex flex-col gap-1 text-xs text-ink-muted" data-testid="EvalView.ModelSettings">
                  {suiteData.targetRef.params && Object.keys(suiteData.targetRef.params).length > 0 && (
                    <span className="font-mono">
                      {Object.entries(suiteData.targetRef.params).map(([k, v]) => `${k}=${String(v)}`).join(' · ')}
                    </span>
                  )}
                  {suiteData.targetRef.system && (
                    <p className="max-w-prose whitespace-pre-wrap leading-relaxed" style={PRETTY}>
                      <span className="font-medium text-gray-600 dark:text-gray-300">System: </span>{suiteData.targetRef.system}
                    </p>
                  )}
                </div>
              )}
            </div>
            {suiteData.description && (
              <div className="min-w-0 sm:col-span-2">
                <SectionLabel icon="pi pi-align-left">Description</SectionLabel>
                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-gray-700 dark:text-gray-300" style={PRETTY}>{suiteData.description}</p>
              </div>
            )}
          </div>
        </Rise>

        {/* Scoring. `metrics` is the current shape; a suite saved before it has
            only `grader`, which the Suite panel above already prints. */}
        {metrics.length > 0 && (
          <Rise i={2} className={`${PANEL} p-4`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <SectionLabel icon="pi pi-chart-bar">Scoring metrics</SectionLabel>
              <span className="text-xs tabular-nums text-ink-muted">
                {overallPass != null && <>overall pass ≥ {overallPass}</>}
                {suiteData.maxLatencyP95Ms != null && <> · p95 latency ≤ {suiteData.maxLatencyP95Ms.toLocaleString()} ms</>}
              </span>
            </div>
            {/* One row per metric, in a table: eight metrics were eight full-width
                cards, and the numbers a reviewer compares — weight, pass bar —
                did not line up from one to the next. */}
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-left text-sm" data-testid="EvalView.Metrics">
                <thead>
                  <tr className="text-[11px] uppercase tracking-[0.05em] text-ink-muted">
                    <th className="py-1.5 pr-4 font-medium">Metric</th>
                    <th className="py-1.5 pr-4 font-medium">Method</th>
                    <th className="py-1.5 pr-4 text-right font-medium">Weight</th>
                    <th className="py-1.5 pr-4 text-right font-medium">Pass ≥</th>
                    <th className="py-1.5 font-medium">Judge</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                  {metrics.map((m, i) => (
                    <tr key={m.key || i} className="align-top">
                      <td className="py-2 pr-4"><StatusChip tone="info" dot={false}>{m.key}</StatusChip></td>
                      <td className="py-2 pr-4 font-mono text-xs text-gray-700 dark:text-gray-300">
                        {m.graderType}
                        {['exact', 'contains', 'regex'].includes(m.graderType) && m.caseSensitive && (
                          <span className="ml-1.5 font-sans text-gray-500">· case-sensitive</span>
                        )}
                        {m.graderType === 'regex' && m.pattern && (
                          <span className="mt-1 block break-all text-ink-muted">/{m.pattern}/</span>
                        )}
                        {m.graderType === 'length' && (
                          <span className="mt-1 block font-sans text-ink-muted">
                            {m.minLength != null && m.maxLength != null ? `${m.minLength}–${m.maxLength} characters`
                              : m.minLength != null ? `at least ${m.minLength} characters` : `at most ${m.maxLength} characters`}
                          </span>
                        )}
                        {m.referenceFree && (
                          <span className="mt-1 block font-sans text-ink-muted">grades from the input alone</span>
                        )}
                        {m.graderType === 'llm_judge' && m.instructions && (
                          <p className="mt-1 max-w-prose whitespace-pre-wrap font-sans text-xs leading-relaxed text-ink-muted" style={PRETTY}>{m.instructions}</p>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">{m.weight ?? 1}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{m.passThreshold ?? 0.7}</td>
                      <td className="py-2 text-xs text-ink-muted">
                        {m.graderType !== 'llm_judge' ? '—'
                          : m.model ? <span className="font-mono">{modelLabel(m.model)}</span>
                          : <span title="No judge is pinned, so the model chosen in the Run dialog judges">the run's model</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Rise>
        )}

        {/* Cases (references into the eval_case library). Each row shows the QUESTION,
            not just a name: "what does this suite actually ask" is the thing a
            reviewer opens it to find out, and names imported from a dataset are
            only the question truncated anyway. */}
        <Rise i={3} className={`${PANEL} p-4`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-baseline gap-2">
              <SectionLabel icon="pi pi-list-check">Cases</SectionLabel>
              <span className="text-xs tabular-nums text-ink-muted">{cases.length}</span>
            </div>
            {cases.length > 8 && (
              <input
                type="search" value={caseFilter} onChange={(e) => setCaseFilter(e.target.value)}
                placeholder="Filter by code, name or question"
                aria-label="Filter cases"
                className="w-64 max-w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-400 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                data-testid="EvalView.CaseFilter"
              />
            )}
          </div>
          {cases.length === 0 ? (
            <EmptyState icon="pi pi-inbox" title="No cases in this suite"
                        hint="Import a dataset (CSV, JSON or JSONL — one row per case), or add cases from the library on the Edit screen." />
          ) : (() => {
            const q = caseFilter.trim().toLowerCase();
            const matching = cases.filter((slot) => {
              if (!q) return true;
              const c = caseById[String(slot.caseRef)];
              const hay = [c?.caseCode, c?.name, typeof c?.input === 'string' ? c.input : ''].join(' ').toLowerCase();
              return hay.includes(q);
            });
            const LIMIT = 50;
            const visible = showAllCases ? matching : matching.slice(0, LIMIT);
            return (
              <>
                <div className="mt-3 flex flex-col gap-2" data-testid="EvalView.Cases">
                  {visible.map((slot, index) => {
                    const c = caseById[String(slot.caseRef)];
                    const types = Array.isArray(c?.type) ? c.type : c?.type ? [c.type as any] : [];
                    const question = typeof c?.input === 'string' ? c.input : c?.input ? JSON.stringify(c.input) : '';
                    const expected = typeof c?.expectedResult === 'string' ? c.expectedResult : '';
                    return (
                      <div
                        key={String(slot.caseRef) || index}
                        className="ui-inset ui-rise flex items-start justify-between gap-3 p-3"
                        style={riseAt(Math.min(index, 8))}
                      >
                        <div className="flex min-w-0 items-start gap-3">
                          <span className="mt-0.5 w-6 flex-none text-right text-xs tabular-nums text-ink-muted">{(slot.order ?? index) + 1}</span>
                          <div className="min-w-0">
                            {c ? (
                              <>
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                  <span className="font-mono text-xs text-indigo-600 dark:text-indigo-400">{c.caseCode}</span>
                                  {types.map((t: string) => <StatusChip key={t} tone="neutral" dot={false}>{t}</StatusChip>)}
                                  {(c.tags || []).includes('DATASET') && <StatusChip tone="info" dot={false}>dataset</StatusChip>}
                                </div>
                                <p className="mt-1 line-clamp-2 text-sm text-ink" style={PRETTY}>{question || c.name}</p>
                                {expected && (
                                  <p className="mt-0.5 line-clamp-1 text-xs text-ink-muted">
                                    <span className="text-ink-muted">expects </span>{expected}
                                  </p>
                                )}
                              </>
                            ) : (
                              <span className="text-sm italic text-gray-500">Case not found ({String(slot.caseRef)}) — it may have been deleted</span>
                            )}
                          </div>
                        </div>
                        {slot.modelIdOverride && (
                          <StatusChip tone="neutral" dot={false} className="flex-none font-mono">{modelLabel(slot.modelIdOverride)}</StatusChip>
                        )}
                      </div>
                    );
                  })}
                </div>
                {q && matching.length === 0 && (
                  <p className="mt-3 text-sm text-ink-muted">No case matches “{caseFilter}”.</p>
                )}
                {!showAllCases && matching.length > LIMIT && (
                  <div className="mt-3 flex justify-center">
                    <span className="ui-press inline-flex">
                      <Button type="button" label={`Show all ${matching.length}`} severity="secondary" text size="small"
                              onClick={() => setShowAllCases(true)} />
                    </span>
                  </div>
                )}
              </>
            );
          })()}
        </Rise>

        <Rise i={4}>
          <AuditInfoCard
            surface="panel"
            createdAt={(suiteData as any).createdAt}
            updatedAt={(suiteData as any).updatedAt}
            createdBy={suiteData.createdBy}
            updatedBy={suiteData.updatedBy}
          />
        </Rise>
      </div>

      <DeleteConfirmDialog
        visible={deleteDialogOpen}
        onCancel={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
        message="Delete this suite? It is marked deleted rather than removed, and its past runs stay on record."
        testId="EvalView.Dialog.Delete"
      />

      <DatasetImportDialog
        suite={suiteData}
        visible={importOpen}
        onHide={() => setImportOpen(false)}
        onImported={() => { reloadSuite(); }}
        onRunRequested={() => setRunDialogOpen(true)}
      />

      <Dialog
        header={`Run Suite — ${suiteData.name}`}
        visible={runDialogOpen}
        onHide={() => setRunDialogOpen(false)}
        style={{ width: '48rem', maxWidth: '95vw' }}
        maximizable
        dismissableMask
        data-testid="EvalView.Dialog.Run"
      >
        {id && <EvalRunTriggerPanel suiteRef={id} />}
      </Dialog>
    </div>
  );
};

export default EvalView;
